import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AdminUser } from '@frappi/shared';
import { api } from '../lib/api';

interface AdminAuthState {
  user: AdminUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshSession: () => Promise<void>;
}

export const useAuthStore = create<AdminAuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: true,

      login: async (email, password) => {
        // The backend's /api/admin/auth/login only accepts admin_users
        // credentials — no role check needed on the client.
        const { token, user } = await api.auth.login(email, password);
        api.setToken(token);
        set({ user, token, isAuthenticated: true, isLoading: false });
      },

      logout: () => {
        api.setToken(null);
        set({ user: null, token: null, isAuthenticated: false, isLoading: false });
      },

      refreshSession: async () => {
        const token = get().token;
        if (!token) {
          set({ isLoading: false });
          return;
        }
        try {
          api.setToken(token);
          const user = await api.auth.me();
          set({ user, isAuthenticated: true, isLoading: false });
        } catch {
          get().logout();
        } finally {
          if (get().isLoading) {
            set({ isLoading: false });
          }
        }
      },
    }),
    {
      name: 'frappi-admin-auth',
      partialize: (state) => ({ user: state.user, token: state.token, isAuthenticated: state.isAuthenticated }),
      onRehydrateStorage: () => (state) => {
        if (state?.token) {
          api.setToken(state.token);
          state.refreshSession();
        } else {
          state?.logout();
        }
      },
    }
  )
);
